import { QUALITY_METRIC_ERROR_CODES } from 'shared/constants/quality-metric.constants';

interface AlreadyExistsError {
  alreadyExists: string[];
}

// A single failure carries one message, a bulk save one per failed user, and a duplicate
// email from `createSingleUser` arrives as `{ alreadyExists }`.
type UserActionError = string | string[] | AlreadyExistsError;

interface UserActionResult {
  errors?: UserActionError | null;
  error?: UserActionError | null;
}

interface UserActionErrorLang {
  ERROR: string;
  ERROR_USERS_ALREADY_EXIST: string;
}

const isAlreadyExistsError = (error: UserActionError): error is AlreadyExistsError =>
  typeof error === 'object' && !Array.isArray(error) && Array.isArray(error.alreadyExists);

export const resolveErrorMessage = (
  result: UserActionResult | null | undefined,
  lang: UserActionErrorLang,
): string | null => {
  const error = result?.errors ?? result?.error;

  if (!error) {
    return null;
  }
  if (isAlreadyExistsError(error)) {
    return error.alreadyExists.length
      ? lang.ERROR_USERS_ALREADY_EXIST.replace('{emails}', error.alreadyExists.join(', '))
      : lang.ERROR;
  }
  if (Array.isArray(error)) {
    return error.filter(Boolean).join('\n') || lang.ERROR;
  }

  return typeof error === 'string' ? error : lang.ERROR;
};

interface ProviderNameErrorLang {
  ERROR_PROVIDER_IDENTIFICATION_TAKEN: string;
  ERROR_PROVIDER_IDENTIFICATION_TAKEN_NAME: string;
}

export interface ProviderNameError {
  message: string;
  /** The conflicting name as the server quoted it, or `null` when it could not be read. */
  name: string | null;
  /** The provider the server named before the quoted value, or `null` when it could not be read. */
  provider: string | null;
}

// The 409 message names the provider and quotes the colliding value: `Provider identification mpog "ROE, R" is …`.
const takenEntry = (
  error: UserActionError | null | undefined,
): { name: string | null; provider: string | null } => {
  const text = typeof error === 'string' ? error : '';
  const name = /"(.+)"/.exec(text)?.[1] ?? null;
  const provider = name ? (/identification (\S+) "/.exec(text)?.[1] ?? null) : null;
  return { name, provider };
};

/**
 * The provider-names field error for a failed user save, or `null` when the failure is not the
 * `409 PROVIDER_IDENTIFICATION_TAKEN` (those keep going through {@link resolveErrorMessage}).
 *
 * @param result - What `updateUser` resolved with; `errorCode` is the server `code`.
 * @param lang - The `USERS_MANAGEMENT` strings.
 * @returns The field message and the name it is about, or `null`.
 */
export const resolveProviderNameError = (
  result: (UserActionResult & { errorCode?: string | number | null }) | null | undefined,
  lang: ProviderNameErrorLang,
): ProviderNameError | null => {
  if (result?.errorCode !== QUALITY_METRIC_ERROR_CODES.PROVIDER_IDENTIFICATION_TAKEN) {
    return null;
  }
  const { name, provider } = takenEntry(result.errors ?? result.error);
  return {
    name,
    provider,
    message: name
      ? lang.ERROR_PROVIDER_IDENTIFICATION_TAKEN_NAME.replace('{name}', name)
      : lang.ERROR_PROVIDER_IDENTIFICATION_TAKEN,
  };
};
